import React, { useEffect, useRef, useState } from "react";
import { GetServerSidePropsContext, NextPage } from "next";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getDispensaryReviews from "api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { CountryCode } from "custom-types/CountryCode";
import { Dispensary } from "custom-types/Dispensary";
import { ConsumerDispensaryReview } from "custom-types/Reviews";
import {
  COMPLIANCE_RULESET_LOAD,
  ComplianceRuleset,
} from "redux/reducers/complianceRules";
import { DISPENSARY_LOAD } from "redux/reducers/dispensary";
import logError from "utils/logError";
import { getFirstQueryParam } from "utils/nextRouterUtils";
import { getCountryCodeHeader } from "utils/requestContext";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";
import { validateDispensary } from "utils/validateDispensary";

import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import NoReviews from "components/Dispensary/Reviews/components/NoReviews";
import ReviewContainer, {
  ReviewType,
} from "components/Dispensary/Reviews/components/ReviewContainer";
import ReviewsHeader from "components/Dispensary/ReviewsHeader";
import PageNavigation from "components/PageNavigation";
import Placeholder, { Rect } from "components/Placeholder";
import Sort from "components/Sort";

import DispensaryBreadcrumbs from "../../../components/Dispensary/DispensaryBreadcrumbs";

export type ReviewPageProps = {
  dispensary: Dispensary;
  initialReviews: ConsumerDispensaryReview[];
  totalReviews: number;
  complianceRuleset: void | ComplianceRuleset;
};
export const DISPENSARY_REVIEWS_PER_PAGE = 24;

/* eslint-disable sort-keys-fix/sort-keys-fix -- exception: order important to display */
export const sortOptions: { [key: string]: string } = {
  "Most Helpful": "upvotes_count:desc",
  "Least Helpful": "upvotes_count:asc",
  Newest: "created:desc",
  Oldest: "created:asc",
};
/* eslint-enable sort-keys-fix/sort-keys-fix -- exception: order important to display */
const ReviewsPage: NextPage<ReviewPageProps> = ({
  dispensary,
  initialReviews,
  totalReviews,
  complianceRuleset,
}) => {
  const router = useRouter();

  const page = Math.max(Number(getFirstQueryParam(router.query.page) ?? 1), 1);
  const sort =
    getFirstQueryParam(router.query.sort) ?? sortOptions["Most Helpful"];

  const [loading, setLoading] = useState<boolean>(false);
  const [dispensaryReviews, setDispensaryReviews] =
    useState<ConsumerDispensaryReview[]>(initialReviews);
  const [reviewCount, setReviewCount] = useState(totalReviews);
  const { featureTier } = dispensary;
  const isInfoTier = featureTier === "info";
  const mounted = useRef(false);
  const dispatch = useDispatch();
  const noReviews = !totalReviews;

  useTrackDispensaryPageViewSplitEvent({ page: "reviews", tier: featureTier });

  useEffect(() => {
    if (complianceRuleset) {
      dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }
    if (dispensary) {
      dispatch({
        dispensary,
        type: DISPENSARY_LOAD,
      });
    }
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);

      const { reviews, totalReviews } = await getDispensaryReviews(
        dispensary.slug,
        (page - 1) * DISPENSARY_REVIEWS_PER_PAGE,
        DISPENSARY_REVIEWS_PER_PAGE,
        encodeURI(sort),
      );
      setDispensaryReviews(reviews);
      setReviewCount(totalReviews);
      setLoading(false);
    };

    // first render will have reviews fetched serverside
    if (mounted.current) {
      fetchReviews();
    }

    mounted.current = true;
  }, [sort, page, dispensary.slug]);

  return (
    <>
      <DispensaryHead
        dispensary={dispensary}
        pageType="reviews"
        noIndex={noReviews}
      />
      <DispensaryHeaderSlim
        dispensary={dispensary}
        reviewCount={reviewCount}
        isInfoTier={isInfoTier}
      />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.reviews}
        isInfoTier={isInfoTier}
        dispensary={dispensary}
      />
      {!noReviews && (
        <>
          <section className="container mt-xl lg:mt-section">
            <ReviewsHeader dispensary={dispensary} />
          </section>
          <section className="container flex flex-col justify-end mb-section mt-xl">
            {(dispensaryReviews.length || loading) && (
              <Sort className="self-end mb-sm" sortOptions={sortOptions} />
            )}
            <span>
              {!loading ? (
                dispensaryReviews.map((review) => (
                  <ReviewContainer
                    review={review}
                    key={review.id}
                    type={ReviewType.DISPENSARY}
                  />
                ))
              ) : (
                <div className="mb-section">
                  <Placeholder width="100%" height="270" className="mb-lg">
                    <Rect width="100%" height="100%" />
                  </Placeholder>
                  <Placeholder width="100%" height="270" className="mb-lg">
                    <Rect width="100%" height="100%" />
                  </Placeholder>
                  <Placeholder width="100%" height="270" className="mb-lg">
                    <Rect width="100%" height="100%" />
                  </Placeholder>
                </div>
              )}
            </span>
            <PageNavigation
              currentPage={page}
              totalPages={Math.ceil(reviewCount / DISPENSARY_REVIEWS_PER_PAGE)}
            />
            <DispensaryBreadcrumbs dispensary={dispensary} />
          </section>
        </>
      )}
      {noReviews && <NoReviews dispensary={dispensary} />}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { query, req, res, resolvedUrl } = context;
  const { slug } = query;

  // Required to fix incorrect url formatting found intermittently with getServerSideProps.
  // occasionally url would contain /next/url/[environment]
  req.url = resolvedUrl;

  try {
    const dispensary = await getDispensary(slug as string);

    const countryCode = getCountryCodeHeader(req.headers) as CountryCode;

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      req,
      res,
    });

    if (dispensary) {
      const page = Math.max(Number(getFirstQueryParam(query.page) ?? 1), 1);
      const sort =
        getFirstQueryParam(query.sort) ?? sortOptions["Most Helpful"];

      const [{ reviews: initialReviews, totalReviews }, complianceRuleset] =
        await Promise.all([
          getDispensaryReviews(
            dispensary.slug,
            (page - 1) * DISPENSARY_REVIEWS_PER_PAGE,
            DISPENSARY_REVIEWS_PER_PAGE,
            encodeURI(sort),
          ),
          getRegionComplianceRules(dispensary?.state, dispensary?.country),
        ]);

      const pageDoesNotExist =
        page > 1 && totalReviews / DISPENSARY_REVIEWS_PER_PAGE < page - 1;

      if (pageDoesNotExist || redirect) {
        return {
          notFound: true,
        };
      }

      return {
        props: {
          complianceRuleset,
          dispensary,
          initialReviews,
          totalReviews,
        },
      };
    }
  } catch (err) {
    logError(`Error processing data in ${resolvedUrl} getServerSideProps`);

    return {
      notFound: true,
      props: {},
    };
  }
};

export default ReviewsPage;
